// src/components/Header.jsx
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import NotificationBell from "./NotificationBell";
import { LogOut, LayoutDashboard, Shield } from "lucide-react";

function SubscriptionBtn({ profile, lang }) {
  // لا يظهر إذا لم يكمل الاختبار
  if (!profile?.adhdProfile) return null;

  // بانتظار الموافقة
  if (profile?.subscription === "paid" && profile?.status === "pending") {
    return (
      <div style={{
        fontSize: "0.75rem", padding: "0.3rem 0.75rem",
        borderRadius: "var(--radius-sm)", fontWeight: 600,
        background: "rgba(234,179,8,0.15)",
        color: "#ffffff",
        border: "1px solid #d97706",
        cursor: "default"
      }}>
        ⏳ {lang === "ar" ? "قيد المراجعة" : "Pending Review"}
      </div>
    );
  }

  // نشط — يعرض الأيام المتبقية
  if (profile?.subscription === "paid" && profile?.status === "active") {
    const days = profile?.daysLeft;
    const isWarning = days != null && days <= 5;
    return (
      <div style={{
        fontSize: "0.75rem", padding: "0.3rem 0.75rem",
        borderRadius: "var(--radius-sm)", fontWeight: 600,
        background: isWarning ? "rgba(220,38,38,0.15)" : "rgba(34,139,34,0.15)",
        color: isWarning ? "#ffffff" : "#ffffff",
        border: `1px solid ${isWarning ? "#dc2626" : "#16a34a"}`,
        cursor: "default"
      }}>
        {isWarning ? "⚠️" : "✅"} {lang === "ar"
          ? (days != null ? `${days} يوم متبقي` : "اشتراكك نشط")
          : (days != null ? `${days} days left` : "Active")}
      </div>
    );
  }

  // منتهي أو مجاني — زر للدفع
  const isExpired = profile?.status === "expired";
  return (
    <Link to="/payment" style={{
      fontSize: "0.75rem", padding: "0.3rem 0.75rem",
      borderRadius: "var(--radius-sm)", fontWeight: 600,
      background: isExpired ? "rgba(220,38,38,0.15)" : "rgba(34,139,34,0.9)",
      color: isExpired ? "#ffffff" : "#fff",
      border: `1px solid ${isExpired ? "#dc2626" : "transparent"}`,
      textDecoration: "none",
      transition: "all 0.2s"
    }}>
      {isExpired
        ? (lang === "ar" ? "🔄 جدد اشتراكك" : "🔄 Renew")
        : (lang === "ar" ? "⭐ اشترك الآن" : "⭐ Subscribe")}
    </Link>
  );
}

export default function Header() {
  const { user, profile, logout } = useAuth();
  const { t, lang, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  const isActive = (path) => location.pathname === path ? "nav-link active" : "nav-link";

  return (
    <header className="header">
      <div className="container header-inner">
        {/* Logo */}
          <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img
              src="/Logo_withoutBG.png"
              alt="نظم"
              style={{ height: 44, width: "auto", objectFit: "contain" }}
            />
          </Link>

        {/* Nav */}
        <nav className="header-nav">
          {/* Language Toggle - always visible */}
          <button
            onClick={toggleLang}
            className="btn btn-ghost btn-sm"
            style={{ fontSize: "0.82rem", fontWeight: 600 }}
            title="تغيير اللغة / Toggle Language"
          >
            {lang === "ar" ? "English" : "العربية"}
          </button>

          {user ? (
            <>
              <NotificationBell />

              {profile?.role === "admin" && (
                <>
                  <Link to="/admin" className={isActive("/admin")}
                    style={{
                      display: "flex", alignItems: "center", gap: "0.25rem",
                      padding: "0.35rem 0.75rem", borderRadius: "var(--radius-sm)",
                      background: isActive("/admin").includes("active") ? "rgba(99,102,241,0.25)" : "transparent",
                      color: "#fff", fontWeight: 600, fontSize: "0.85rem",
                      transition: "all 0.2s", textDecoration: "none"
                    }}
                  >
                    <Shield size={16} />
                    <span style={{ marginRight: "0.25rem", marginLeft: "0.25rem" }}>{t("adminDashboard")}</span>
                  </Link>
                  <Link to="/doctor" className={isActive("/doctor")}>
                    <LayoutDashboard size={16} />
                    <span style={{ marginRight: "0.25rem", marginLeft: "0.25rem" }}>{t("dashboard")}</span>
                  </Link>
                </>
              )}

              {profile?.role === "doctor" && (
                <Link to="/doctor" className={isActive("/doctor")}>
                  <LayoutDashboard size={16} />
                  <span style={{ marginRight: "0.25rem", marginLeft: "0.25rem" }}>{t("dashboard")}</span>
                </Link>
              )}

              {profile?.role === "parent" && (
                <>
                  {/* زر حالة الاشتراك */}
                  <SubscriptionBtn profile={profile} lang={lang} />
                  
                  {/* استشارة مدفوعة — فقط للمشتركين النشطين */}
                  {profile?.subscription === "paid" && profile?.status === "active" && (
                    <Link to="/consult/paid" className={isActive("/consult/paid")}>
                      {t("paidConsult")}
                    </Link>
                  )}

                  <Link to="/consult/free" className={isActive("/consult/free")}>
                    {t("freeConsult")}
                  </Link>
                </>
              )}

              <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.98)" }}>
                  {profile?.name?.split(" ")[0] || user.email}
                </span>
                <button onClick={handleLogout} className="btn btn-ghost btn-sm" title={t("logout")}>
                  <LogOut size={16} />
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className={isActive("/login")}>{t("login")}</Link>
              <Link to="/register" className="btn btn-primary btn-sm">{t("register")}</Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
